import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn, isEmptyOrSpaces } from '@/utils'
import { useShallow } from 'zustand/react/shallow'
import { useSearchParams } from 'wouter'
import { useCADPageStore } from '@/state/cad'
import {
  useDeleteOperationStep,
  useDeletePartsFromOperation,
} from '@/services/queries/operation_steps'
import { useCADQuery } from '@/services/queries/cads'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useStepOrderNumber } from './hooks/useStepOrderNumber'

import { Button } from '@/components/ui/button'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CollapsibleTriangleIcon,
} from '@/components/ui/collapsible'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { OpenDocumentPageButton } from './OpenDocumentPageButton'
import { SecondaryActionsButton } from './SecondaryActionsButton'
import { StepCardScreenshotsButton } from './StepCardScreenshotsButton'
import { AddPartFromCADButton } from './AddPartFromCADButton'
import { StepPart } from './StepPart'
import { StepCardContainer } from './StepCardContainer'
import { StepCardNote } from './StepCardNote'

import type { Step } from '@/services/queries/operation_steps/types'
import { Selectable, useSelectable } from '../../hooks'
import { RenameDialog } from '@/components/core/RenameDialog'
import { useUpdateDocumentPage } from '@/services/queries/document_pages'
import { ASMTreeNode } from '@/state'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { AddPartIcon } from '@/components/icons/AddPartIcon'
import { EditableField } from '@/components/core/EditableField'
import { ConfirmationDialog } from '@/components/core/ConfirmationDialog'

export const StepCard = ({
  stepNumber,
  step,
  isReadOnly,
}: {
  stepNumber: number
  step: Step
  isReadOnly?: boolean
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isRenaming, setIsRenaming] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const { isLoading: isCadLoading, data: cadData } = useCADQuery()
  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()
  const {
    isLoading: isLoadingOrderNumber,
    data: { orderNumber },
  } = useStepOrderNumber({
    step,
  })
  const { mutate: deleteOperationStep, isPending: isDeletingOperation } =
    useDeleteOperationStep()
  const { mutate: updateOperation, isPending: isUpdatingOperation } =
    useUpdateDocumentPage()
  const { mutateAsync: deletePartsFromOperation } =
    useDeletePartsFromOperation()
  const activeOperationStep = useCADPageStore((state) => state.operationStep)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const onOpenRenameDialog = useCallback(() => {
    setTimeout(() => setIsRenaming(true), 200)
  }, [])

  const onCloseRenameDialog = useCallback(() => {
    setTimeout(() => setIsRenaming(false), 200)
  }, [])
  const updateQueryParams = useCallback(
    (newParams: { [key: string]: string | null }): void => {
      const updated = new URLSearchParams(searchParams.toString())

      Object.keys(newParams).forEach((key) => {
        const value = newParams[key]
        if (value === null) {
          updated.delete(key)
        } else {
          updated.set(key, value)
        }
      })

      setSearchParams(updated)
    },
    [searchParams, setSearchParams],
  )

  const gltf = cadData.gltf
  const cadVersionId = cadData.version?.id
  const assemblyTree = cadData.rawAssemblyTree || { root: '', nodes: [] }
  const assemblyGroupIds = step.assembly_group_ids

  const isProjectTracker = useMemo(
    () => docData?.documentType === 'project_tracker',
    [docData?.documentType],
  )

  const isWorkInstructions = useMemo(
    () => docData?.documentType === 'work_instructions',
    [docData?.documentType],
  )

  const selectable = useSelectable({})
  const isStepSelected = useMemo(
    () => activeOperationStep?.stepId === step.id,
    [activeOperationStep?.stepId, step.id],
  )
  const activeSelectFromCADStep = useMemo(
    () => isStepSelected && activeOperationStep?.selectFromCad,
    [isStepSelected, activeOperationStep?.selectFromCad],
  )
  const parts = useMemo(
    () =>
      assemblyTree?.nodes?.filter((node) =>
        assemblyGroupIds.includes(node.uuid),
      ) || [],
    [assemblyTree?.nodes, assemblyGroupIds],
  )

  const selectedPartsIds = useMemo(
    () =>
      parts
        .filter((part) => selectable.isSelected(part.uuid))
        .map((part) => part.uuid),
    [selectable, parts],
  )

  const partsGroups = useMemo(
    () =>
      parts.reduce(
        (accum, part) => {
          const foundPart = accum.find((p) => p.name === part.display_name)
          if (foundPart) {
            foundPart.parts.push(part)
          } else {
            accum.push({
              name: part.display_name,
              parts: [part],
            })
          }
          return accum
        },
        [] as Array<{ name: string; parts: Array<ASMTreeNode> }>,
      ),
    [parts],
  )

  const selectedPartsIndexes = useMemo(() => {
    return partsGroups.reduce((accum, group, index) => {
      const isAllGroupSelected = group.parts.every((p) =>
        selectable.isSelected(p.uuid),
      )
      if (isAllGroupSelected) {
        accum.push(index)
      }
      return accum
    }, [] as Array<number>)
  }, [selectable, partsGroups])

  const onDelete = useCallback(async () => {
    const documentPageAssemblyReferenceIds = step.documentpageassemblygroupids
      .filter((id) => selectedPartsIds.includes(id.assembly_group_id))
      .map((ref) => ref.id) as Array<number>
    if (documentPageAssemblyReferenceIds.length) {
      if (step.id) {
        await deletePartsFromOperation({
          partsIndex: selectedPartsIndexes,
          cadVersionId: cadVersionId as string,
          assemblyGroupIds: selectedPartsIds,
          documentPageId: step.id,
          documentVersionId: step.document_version,
          documentPageAssemblyReferenceIds,
        })
      }
    }
  }, [
    deletePartsFromOperation,
    step,
    selectedPartsIndexes,
    selectedPartsIds,
    cadVersionId,
  ])

  const onDeleteHandler = useCallback(
    (e: KeyboardEvent) => {
      if (isEditing) {
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        void onDelete()
      }
    },
    [isEditing, onDelete],
  )

  useEffect(() => {
    if (selectedPartsIds?.length) {
      document.addEventListener('keydown', onDeleteHandler)
    }

    return () => {
      document.removeEventListener('keydown', onDeleteHandler)
    }
  }, [selectedPartsIds, onDeleteHandler])

  const isLoading = isCadLoading || isDocumentLoading || isLoadingOrderNumber

  const onDeleteOperation = useCallback(() => {
    const isActive = activeOperationStep?.stepId === step.id
    if (isActive) {
      setCadPageState({
        operationStep: null,
      })
    }
    deleteOperationStep({ stepId: step.id as string })
    const pageId = searchParams.get('page_id')
    if (pageId === step.id) {
      updateQueryParams({ page_id: null })
    }
  }, [
    activeOperationStep?.stepId,
    deleteOperationStep,
    searchParams,
    step.id,
    setCadPageState,
    updateQueryParams,
  ])

  const onCloseDialogHandler = useCallback(() => {
    setTimeout(() => {
      setShowConfirmationModal(false)
    })
  }, [])

  const stepName = isEmptyOrSpaces(step.name)
    ? `${isProjectTracker ? 'Note' : 'Operation'} ${orderNumber}`
    : step.name

  if (isLoading) {
    return (
      <div className="border border-gray-300 rounded-lg p-3 bg-white text-sm">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  return (
    <StepCardContainer step={step} gltf={gltf} stepNumber={stepNumber}>
      <div
        data-testid={`step-card-${stepNumber}`}
        className="flex items-center justify-between w-full"
      >
        <div className="flex items-center space-x-2 pl-1">
          <div
            className={cn(
              'bg-gray-100 text-black rounded-lg px-2 pt-1 inline-block max-w-[145px]',
              {
                'bg-primary-50 text-white':
                  isStepSelected && !activeSelectFromCADStep,
                'bg-purple-800 text-white': activeSelectFromCADStep,
              },
            )}
          >
            <ConfirmationDialog
              title="Are you sure?"
              onConfirm={onDeleteOperation}
              isOpen={showConfirmationModal}
              isLoading={isDeletingOperation}
              closeDialog={onCloseDialogHandler}
            >
              <span className="text-center">
                Once this operation is deleted, it cannot be undone.
              </span>
            </ConfirmationDialog>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="max-w-[135px]">
                  {isReadOnly ? (
                    <div className="text-ellipsis overflow-hidden whitespace-nowrap truncate">
                      {stepName}
                    </div>
                  ) : (
                    <EditableField
                      value={stepName}
                      shouldStopPropagation={isStepSelected}
                      onSaveHandler={(name: string) => {
                        updateOperation({
                          documentPageId: step.id as string,
                          values: {
                            name,
                            template_values: step.template_values,
                            parent_id: step.parent as string,
                          },
                        })
                      }}
                    />
                  )}
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white">
                  <p className="max-w-[250px] text-wrap">
                    {isEmptyOrSpaces(step.name)
                      ? `${isProjectTracker ? 'Note' : 'Operation'} ${orderNumber}`
                      : step.name}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <div className="mr-1">
            <StepCardScreenshotsButton documentPageId={step.id as string} />
          </div>
          <OpenDocumentPageButton documentPageId={step.id as string} />
          {isWorkInstructions && !isReadOnly && (
            <AddPartFromCADButton step={step} />
          )}
          {!isReadOnly && (
            <>
              <RenameDialog
                isOpen={isRenaming}
                isLoading={isUpdatingOperation}
                closeDialog={onCloseRenameDialog}
                onSaveName={(name: string) => {
                  updateOperation({
                    documentPageId: step.id as string,
                    values: {
                      name,
                      template_values: step.template_values,
                      parent_id: step.parent as string,
                    },
                  })
                }}
                initialName={
                  isEmptyOrSpaces(step.name)
                    ? `Operation ${orderNumber}`
                    : step.name
                }
              />
              <SecondaryActionsButton
                onRename={onOpenRenameDialog}
                stepNumber={stepNumber}
                onDelete={() => {
                  if (!parts.length) {
                    onDeleteOperation()
                  } else {
                    setShowConfirmationModal(true)
                  }
                }}
              />
            </>
          )}
        </div>
      </div>

      <div className="py-2">
        {gltf &&
          cadVersionId &&
          partsGroups.length > 0 &&
          partsGroups.map((group, i) => {
            return (
              <StepGroup
                setIsEditing={setIsEditing}
                key={i}
                index={i}
                group={group}
                selectFromCad={Boolean(activeOperationStep?.selectFromCad)}
                gltf={gltf}
                isReadOnly={!!isReadOnly}
                isActive={activeOperationStep?.stepId === step.id}
                step={step}
                selectable={selectable}
                cadversionId={cadVersionId}
              />
            )
          })}
        {parts.length === 0 && isWorkInstructions && (
          <div className="flex flex-col space-y-2 text-wrap px-2">
            <div>
              <span className="font-semibold">
                Add a part to this step to start creating operation pages.
              </span>
            </div>
            <div>
              <span className="font-semibold">You may add parts by:</span>
            </div>
            <div>
              <ul className="list-disc pl-6 flex flex-col space-y-1">
                <li>Drag and drop parts from the Assembly Tree</li>
                <li>
                  Turn on{' '}
                  <span className="inline-block font-semibold align-middle">
                    <AddPartIcon className="w-4 h-4 align-middle" />
                  </span>{' '}
                  &ldquo;Add Parts from CAD&rdquo; mode and click on the parts
                  in CAD
                </li>
              </ul>
            </div>
          </div>
        )}
        {isProjectTracker && (
          <StepCardNote step={step} isReadOnly={isReadOnly} />
        )}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          className="flex items-center space-x-0 text-primary-50 text-xs h-6 rounded-full px-0 hover:bg-white hover:text-primary-50"
          onMouseDown={(e) => {
            e.stopPropagation()
            setCadPageState({
              selectedParts: [],
              subAssemblyDocumentPageId: step.id as string,
            })
          }}
        >
          <span>Suboperation</span>
          <ChevronRightIcon className="w-4 h-4" />
        </Button>
      </div>
    </StepCardContainer>
  )
}

type StepGroupProps = {
  group: {
    name: string
    parts: ASMTreeNode[]
  }
  selectFromCad: boolean
  gltf: GLTFObject
  isReadOnly: boolean
  isActive: boolean
  selectable: Selectable
  step: Step
  cadversionId: string
  index: number
  setIsEditing: (isEditing: boolean) => void
}
const StepGroup = ({
  group,
  selectFromCad,
  gltf,
  isReadOnly,
  isActive,
  step,
  selectable,
  cadversionId,
  index,
  setIsEditing,
}: StepGroupProps) => {
  const parts = useMemo(() => group.parts, [group.parts])
  const { mutateAsync: deletePartsFromOperation } =
    useDeletePartsFromOperation()
  const onDelete = useCallback(
    async (partsUUIDs: Array<string>) => {
      const documentPageAssemblyReference =
        step.documentpageassemblygroupids.find((id) =>
          partsUUIDs.includes(id.assembly_group_id),
        )
      if (documentPageAssemblyReference?.id) {
        if (step.id) {
          await deletePartsFromOperation({
            partsIndex: parts.length > 1 ? undefined : [index],
            cadVersionId: cadversionId,
            assemblyGroupIds: partsUUIDs,
            documentPageId: step.id,
            documentVersionId: step.document_version,
            documentPageAssemblyReferenceIds: [
              documentPageAssemblyReference.id,
            ],
          })
        }
      }
    },
    [deletePartsFromOperation, step, index, parts.length, cadversionId],
  )

  const isGroupSelected = useMemo(
    () => parts.every((part) => selectable.isSelected(part.uuid)),
    [parts, selectable.size],
  )

  return parts.length > 1 ? (
    <Collapsible>
      <div className="flex items-center space-x-2">
        <CollapsibleTrigger
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            className={cn('p-0 pr-1 flex-1 justify-start', {
              'bg-primary-20': isGroupSelected,
            })}
          >
            <CollapsibleTriangleIcon className="w-5 h-5 text-indigo-400" />
            <div className="flex font-semibold w-full justify-between">
              <span className="text-ellipsis overflow-hidden max-w-[205px]">
                {group.name}{' '}
              </span>
              <span>(x{parts.length})</span>
            </div>
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div
          className={cn('pl-4', {
            'bg-primary-20': isGroupSelected,
          })}
        >
          {parts.map((part, i) => (
            <StepPart
              setIsEditing={setIsEditing}
              key={i}
              selectFromCad={selectFromCad}
              part={part}
              gltf={gltf}
              isReadOnly={isReadOnly}
              onMouseDown={selectable.handlers(part.uuid, 'step').onSelectStart}
              stepIsActive={isActive}
              isSelected={selectable.isSelected(part.uuid)}
              onDelete={onDelete}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  ) : (
    <StepPart
      setIsEditing={setIsEditing}
      selectFromCad={selectFromCad}
      part={parts[0]}
      gltf={gltf}
      isReadOnly={isReadOnly}
      onMouseDown={selectable.handlers(parts[0].uuid, 'step').onSelectStart}
      stepIsActive={isActive}
      isSelected={selectable.isSelected(parts[0].uuid)}
      onDelete={onDelete}
    />
  )
}
